import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { HiX } from "react-icons/hi";
import { APP_NAME } from "constants/app.constant";
import getWhitelabelLogoMode, {
  getWhitelabelSidenavLogoLayout,
} from "utils/getWhitelabelLogoMode";

const LOGO_SRC_PATH = "/img/logo/";
export const MOLE_LOGO_FULL = `${LOGO_SRC_PATH}logo-light-full.png`;
export const MOLE_LOGO_STREAMLINE = `${LOGO_SRC_PATH}logo-light-streamline.svg`;

const LogoDivider = ({ compact }) => (
  <HiX
    className={classNames(
      "shrink-0 text-gray-400 dark:text-gray-500",
      compact ? "h-3 w-3" : "h-4 w-4",
    )}
    aria-hidden
  />
);

/** Auth: horizontal company × mole */
const WhitelabelHorizontal = ({ companyLogoSrc }) => (
  <div className="flex w-full min-w-0 items-center justify-center gap-3">
    <div className="flex h-10 min-w-0 flex-1 items-center justify-center sm:h-11">
      <img
        src={companyLogoSrc}
        alt="Company logo"
        className="h-full w-auto max-w-full object-contain"
        draggable={false}
      />
    </div>
    <LogoDivider />
    <div className="flex h-10 min-w-0 flex-1 items-center justify-center sm:h-11">
      <img
        src={MOLE_LOGO_FULL}
        alt={`${APP_NAME} logo`}
        // Crown on Mole mark makes optical center sit low — nudge up vs partner logo
        className="-mt-1.5 sm:-mt-3.5 h-full w-auto max-w-full object-contain"
        draggable={false}
      />
    </div>
  </div>
);

/** Chrome header / sidenav top: company logo only */
const WhitelabelCompanyOnly = ({ companyLogoSrc, collapsed, isSidenav }) => (
  <div className="flex w-full items-center justify-center">
    <img
      src={companyLogoSrc}
      alt="Company logo"
      className={classNames(
        "object-contain",
        collapsed
          ? "max-h-9 max-w-[40px]"
          : isSidenav
            ? "max-h-14 max-w-[200px]"
            : "h-10 max-h-12 w-auto max-w-full",
      )}
      draggable={false}
    />
  </div>
);

const Logo = (props) => {
  const { type, mode, gutter, className, imgClass, style, logoWidth, variant } =
    props;
  const whitelabel = useSelector((state) => state.base.common.whitelabel);
  const domain = window.location.host.slice(
    0,
    window.location.host.indexOf("."),
  );
  const isVarta = domain.includes("varta");

  const logoMode = getWhitelabelLogoMode(whitelabel?.config);
  const sidenavLogoLayout = getWhitelabelSidenavLogoLayout(whitelabel?.config);
  const companyLogoSrc = whitelabel?.company?.logo;
  const useCompanyLogo =
    whitelabel?.active &&
    (logoMode === "standalone" || logoMode === "withMole") &&
    !!companyLogoSrc;

  const moleSrc = type === "full" ? MOLE_LOGO_FULL : MOLE_LOGO_STREAMLINE;
  const isSidenav = variant === "sidenav";
  const collapsed = type !== "full";
  const hideCompanyLogoInSidenav =
    isSidenav &&
    logoMode === "withMole" &&
    sidenavLogoLayout === "none";

  const renderCompanyLogo = () => {
    if (logoMode === "standalone") {
      return (
        <WhitelabelCompanyOnly
          companyLogoSrc={companyLogoSrc}
          collapsed={collapsed}
          isSidenav={isSidenav}
        />
      );
    }

    // withMole
    if (isSidenav) {
      if (collapsed) {
        return (
          <WhitelabelCompanyOnly
            companyLogoSrc={companyLogoSrc}
            collapsed
            isSidenav
          />
        );
      }
      if (sidenavLogoLayout === "horizontal") {
        return <WhitelabelHorizontal companyLogoSrc={companyLogoSrc} />;
      }
      // footer: company only in header; Mole rendered in SideNav footer
      return (
        <WhitelabelCompanyOnly
          companyLogoSrc={companyLogoSrc}
          collapsed={false}
          isSidenav
        />
      );
    }

    if (collapsed) {
      return (
        <WhitelabelCompanyOnly
          companyLogoSrc={companyLogoSrc}
          collapsed
          isSidenav={false}
        />
      );
    }

    return <WhitelabelHorizontal companyLogoSrc={companyLogoSrc} />;
  };

  return (
    <div
      className={classNames("logo", className, gutter)}
      style={{
        ...style,
        width: logoWidth,
      }}
    >
      {useCompanyLogo && !hideCompanyLogoInSidenav ? (
        renderCompanyLogo()
      ) : isVarta ? (
        <div className="flex justify-center">
          <img
            className={classNames(
              imgClass,
              `${type === "full" ? "!w-72" : ""} max-w-none mx-auto`,
            )}
            src={`${LOGO_SRC_PATH}varta-logo-${type}.png`}
            alt={`${APP_NAME} logo`}
          />
        </div>
      ) : (
        <img className={imgClass} src={moleSrc} alt={`${APP_NAME} logo`} />
      )}
    </div>
  );
};

Logo.defaultProps = {
  mode: "light",
  type: "full",
  logoWidth: "auto",
  variant: "default",
};

Logo.propTypes = {
  mode: PropTypes.oneOf(["light", "dark"]),
  type: PropTypes.oneOf(["full", "streamline"]),
  variant: PropTypes.oneOf(["default", "sidenav"]),
  gutter: PropTypes.string,
  imgClass: PropTypes.string,
  logoWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default Logo;
